import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
// Deferred for v1 — `bioAgeProjection` is not shipped by the current
// `/api/v2/Precision/Report/GetConsultation` endpoint (discrepancy #2 in
// claude_docs/CONSULTATION_BACKEND_BRIEF.md). Re-enable once the backend
// provides `{ targetBioAge, weeksToTarget, totalImprovementYears }`.
// import BioAgeReductionCard from "./BioAgeReductionCard";
import NutritionPanel from "./NutritionPanel";
import TrainingPanel from "./TrainingPanel";
import BioOptimizationGrid from "./BioOptimizationGrid";

/**
 * Section 3 — Personalized Prescription. Visual reference: Figma node
 * 14687:138506.
 *
 * Layout:
 *   - Title: "Your Personalized Prescription"
 *   - Bio-age progress card (current → +N weeks → target)
 *   - Progressive disclosure: each sub-step appends the next panel below.
 *     Order: bio-optimization → +nutrition → +training.
 */
const ProgrammingSection: React.FC = () => {
  const theme: Theme = useTheme();
  const { stepper } = useConsultation();
  const subId = stepper.current.sub.id;

  const showBioOpt = true;
  const showNutrition = subId === "nutrition" || subId === "training";
  const showTraining = subId === "training";

  return (
    <Box component="section" sx={styles.wrap}>
      <Box component="h1" sx={styles.title(theme)}>
        Your Personalized Prescription
      </Box>
      {/* Deferred for v1 — bio-age projection card hidden until the backend
          ships `bioAgeProjection` (discrepancy #2 in CONSULTATION_BACKEND_BRIEF.md). */}
      {/* <BioAgeReductionCard /> */}
      {/* Anchor ids used by `ConsultationShell`'s scroll effect to bring
          the matching panel into view when the user advances sub-steps
          within Section 3. */}
      {showBioOpt && (
        <Box id="consultation-bio-opt-anchor" sx={styles.anchorWrap}>
          <BioOptimizationGrid />
        </Box>
      )}
      {showNutrition && (
        <Box id="consultation-nutrition-anchor" sx={styles.anchorWrap}>
          <NutritionPanel />
        </Box>
      )}
      {showTraining && (
        <Box id="consultation-training-anchor" sx={styles.anchorWrap}>
          <TrainingPanel />
        </Box>
      )}
    </Box>
  );
};

export default ProgrammingSection;

const styles = {
  wrap: {
    width: "100%",
    maxWidth: "1398px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "24px",
    // Breathing room above the fixed stepper bar — without this the bottom
    // card sits flush against the stepper's drop shadow.
    paddingBottom: "32px",
  },
  title: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "24px",
    lineHeight: 1.6,
    color: theme.colorTokens.N[100],
    textAlign: "center" as const,
  }),
  anchorWrap: {
    width: "100%",
    scrollMarginTop: "16px",
  },
};
